import { useCallback } from 'react';
import ToastUtils from '@/utils/ToastUtils';
import { handleMultimodalDocument, handleMultimodalDocumentToken } from '@/api/grt/ai';
import { handleGetcourseDetail } from '@/api/grt/courseCommend';

/**
 * 多模态视频处理方法 Hook
 * 提供视频点击、详情查看等处理方法
 */
const useMultimodalVideoHandlers = () => {
    /**
     * 创建视频处理方法
     */
    const createVideoHandlers = useCallback(({ data = [], agentName, setDetailData, setShowVideoList, handleBackToList }) => {
        // 处理视频列表中的"查看视频"按钮点击
        const handleVideoClick = async (video, index, videoListFromModal = null) => {
            try {
                const resourceId = video?.id;
                if (!resourceId) {
                    ToastUtils.showToast('缺少资源ID，无法打开视频');
                    console.warn('查看视频时缺少资源ID:', video);
                    return;
                }

                // 1️⃣ 调用多模态视频详情接口，获取播放基础信息 / 资产ID
                const docRes = await handleMultimodalDocument({ id: resourceId });

                if (!docRes?.success || !docRes.data) {
                    ToastUtils.showToast(docRes?.msg || '获取视频信息失败');
                    return;
                }

                const docData = docRes.data;

                // 优先使用后端直接返回的播放地址
                let playUrl =
                    docData.play_url ||
                    docData.video_url ||
                    docData.url ||
                    docData.playUrl ||
                    docData.videoUrl ||
                    docData.default_url ||
                    docData.defaultUrl ||
                    '';

                // 取 vod_id / 资产ID，用于获取播放 token（后端约定使用 vod_id 获取 token）
                const vodId = docData.vod_id;
                const assetId = docData.asset_id || docData.assetId || docData.vod_asset_id || vodId;

                // 2️⃣ 如果有 vodId / 资产ID，则再调用 token 接口，拼接 token
                if (assetId) {
                    try {
                        // 优先使用 vod_id 作为参数名，兼容 asset_id
                        const tokenRes = await handleMultimodalDocumentToken(vodId ? { vod_id: vodId } : { asset_id: assetId });

                        if (tokenRes) {
                            playUrl = `${playUrl}?MtsHlsUriToken=${tokenRes}&token=${tokenRes}`;
                        }
                    } catch (tokenError) {
                        console.error('获取视频 token 失败:', tokenError);
                        // token 获取失败时，仍然尝试使用基础播放地址
                    }
                }

                if (!playUrl) {
                    ToastUtils.showToast('未获取到可用的视频播放地址');
                    return;
                }

                // 获取视频列表数据：优先使用从 Modal 传递的列表，否则使用组件中的 data
                const rawVideoList =
                    Array.isArray(videoListFromModal?.list) && videoListFromModal.list.length > 0
                        ? videoListFromModal.list
                        : Array.isArray(videoListFromModal) && videoListFromModal.length > 0
                          ? videoListFromModal
                          : Array.isArray(data) && data.length > 0
                            ? data
                            : [];
                const themeColor = videoListFromModal?.themeColor || document.body?.getAttribute?.('data-theme') || 'blue';

                const videoListPayload = rawVideoList.map((item) => ({
                    id: item.id,
                    title: item.title,
                    description: item.description || '',
                    duration: item.duration || '',
                    thumbnail: item.image_url || item.thumbnail || '',
                    videoUrl: item.video_url || item.videoUrl || ''
                }));

                let videoListKey = '';
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    try {
                        videoListKey = `videoList_${Date.now()}_${resourceId || ''}`;
                        const videoListStr = JSON.stringify(videoListPayload);
                        window.sessionStorage.setItem(videoListKey, videoListStr);
                        // 验证是否写入成功
                        const verifyData = window.sessionStorage.getItem(videoListKey);
                        if (verifyData === videoListStr) {
                        } else {
                            console.warn('⚠️ sessionStorage 写入验证失败，将使用 URL 参数');
                            videoListKey = '';
                        }
                    } catch (storageError) {
                        console.warn('❌ 缓存视频列表失败:', storageError);
                        videoListKey = '';
                    }
                }

                // 3️⃣ 打开独立的视频播放页（新窗口），传递播放地址等信息
                const videoTitle = video?.title || docData?.name || '视频播放';
                const videoPoster = video?.thumbnail || video?.image_url || docData?.extra_obs_id || docData?.poster || '';
                const videoDescription = video?.description || docData?.description || '';

                // 同时传递 videoListKey 和 videoList，确保新窗口能获取到数据
                const queryParams = {
                    videoUrl: playUrl,
                    title: videoTitle,
                    poster: videoPoster || '',
                    description: videoDescription || '',
                    theme: themeColor
                };
                if (agentName) {
                    queryParams.agentName = agentName;
                }

                // 优先使用 sessionStorage，但同时也传递 URL 参数作为备用
                if (videoListKey) {
                    queryParams.videoListKey = videoListKey;
                }
                // 始终传递 videoList 作为备用方案
                queryParams.videoList = JSON.stringify(videoListPayload);

                const query = new URLSearchParams(queryParams).toString();

                const videoPageUrl = `${window.location.origin}/agent/web/o/video/${resourceId}?${query}`;
                window.open(videoPageUrl, '_blank', 'noopener,noreferrer');
            } catch (error) {
                console.error('查看视频失败:', error);
                ToastUtils.showToast(error?.message || '打开视频失败，请稍后重试');
            }
        };

        // 处理视频列表中的"详情"按钮点击
        const handleVideoDetailClick = async (video, index) => {
            // 获取 resource_id，优先使用 video.id，如果没有则使用 video.resource_id
            const resourceId = video.id;

            if (!resourceId) {
                ToastUtils.showToast('缺少资源ID，无法获取详情');
                console.warn('视频数据缺少 resource_id:', video);
                return;
            }

            // 先显示加载状态
            const loadingVideoDetailData = {
                title: video.title,
                type: 'video_detail',
                video: {
                    ...video,
                    detailInfo: null,
                    loading: true
                },
                videoIndex: index
            };
            if (setDetailData) {
                setDetailData(loadingVideoDetailData);
            }
            if (setShowVideoList) {
                setShowVideoList(false);
            }

            try {
                // 调用接口获取视频详情
                const response = await handleGetcourseDetail({
                    resource_id: resourceId,
                    state: 1 // 默认显示所属业务
                });
                if (response?.success && response?.data) {
                    const apiData = response.data;

                    // 将 API 返回的数据映射到组件期望的格式
                    // 处理 mind_map_summary_list：可能是对象数组（树形结构）或字符串数组
                    let mindMapData = null;
                    if (apiData.mind_map_summary_list && apiData.mind_map_summary_list.length > 0) {
                        const firstItem = apiData.mind_map_summary_list[0];
                        // 判断是对象（树形结构）还是字符串
                        if (typeof firstItem === 'object' && firstItem !== null && firstItem.title) {
                            // 如果是对象数组，取第一个对象（已经是树形结构）
                            mindMapData = firstItem;
                        } else if (typeof firstItem === 'string') {
                            // 如果是字符串数组，取第一个字符串
                            mindMapData = firstItem;
                        }
                    }

                    const videoWithDetail = {
                        ...video,
                        detailInfo: {
                            keywords: apiData.keywords || [],
                            summary: apiData.paragraph_summary || '',
                            mindMap: mindMapData,
                            mindMapList: apiData.mind_map_summary_list || [], // 保存完整的列表，以便后续使用
                            resource_name: apiData.resource_name,
                            template_names: apiData.template_names || []
                        },
                        loading: false
                    };

                    // 构建视频详情数据
                    const videoDetailData = {
                        title: apiData.resource_name || video.title,
                        type: 'video_detail',
                        video: videoWithDetail,
                        videoIndex: index
                    };

                    // 🔥 设置本地详情数据（保持面板打开状态）
                    if (setDetailData) {
                        setDetailData(videoDetailData);
                    }
                } else {
                    // 接口返回失败，例如后端提示"该视频已不存在"
                    // 只做提示，并返回到视频列表，不进入空白的视频详情页
                    ToastUtils.showToast(response?.msg || '获取视频详情失败');
                    if (handleBackToList) {
                        handleBackToList();
                    }
                }
            } catch (error) {
                console.error('获取视频详情失败:', error);
                // 异常场景下同样只提示错误，并回到视频列表
                ToastUtils.showToast(error?.message || '获取视频详情失败');
                if (handleBackToList) {
                    handleBackToList();
                }
            }
        };

        // 处理"查看下一个"按钮点击（在视频详情页面）
        const handleViewNext = async (currentData) => {
            // 使用对话返回的视频数据
            const videoList = Array.isArray(data) && data.length > 0 ? data : [];

            if (videoList.length === 0) {
                console.warn('没有视频数据');
                return;
            }

            // 获取当前视频的索引
            const currentIndex = currentData?.videoIndex;
            if (currentIndex === undefined) {
                console.warn('没有视频索引信息');
                return;
            }

            // 计算下一个视频的索引（循环到第一个）
            const nextIndex = (currentIndex + 1) % videoList.length;
            const nextVideo = videoList[nextIndex];

            // 调用 handleVideoDetailClick 来获取下一个视频的详情
            await handleVideoDetailClick(nextVideo, nextIndex);
        };

        // 处理"返回视频列表"按钮点击
        const handleBackToListHandler = () => {
            if (handleBackToList && typeof handleBackToList === 'function') {
                handleBackToList();
            } else if (setDetailData && setShowVideoList) {
                // 如果没有传入 handleBackToList，使用默认逻辑
                setDetailData({
                    title: '视频列表',
                    type: 'video_list',
                    description: ''
                });
                setShowVideoList(true);
            }
        };

        return {
            handleVideoClick,
            handleVideoDetailClick,
            handleViewNext,
            handleBackToList: handleBackToListHandler
        };
    }, []);

    return {
        createVideoHandlers
    };
};

export default useMultimodalVideoHandlers;
